import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from "@angular/core";
import { ThermostatHVACMode } from "src/components/shared-components/enum/enumThermostatHVACMode";
import { ISliderColor } from "src/components/shared-components/interfaces/ISliderColor";
import { MqttClientService } from "src/service/mqtt-client.service";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { LoggerService } from "src/service/logger.service";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ComponentAdapterThermostat } from "src/components/shared-components/class/adapters/component-adapter-thermostat";
import { IconHelper } from "src/components/shared-components/class/icon-helper";
import { IonIcon, IonToggle } from "@ionic/angular";
import { DraggableSliderComponent } from "../../draggable-slider/draggable-slider.component";

@Component({
    selector: "app-thermostat-expanded-section",
    templateUrl: "./thermostat-expanded-section.component.html",
    styleUrls: ["./thermostat-expanded-section.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonIcon, IonToggle, DraggableSliderComponent, TranslatePipe]
})
export class ThermostatExpandedSectionComponent implements OnInit {
  private readonly _mqttService = inject(MqttClientService);
  private readonly _translateService = inject(TranslateService);
  private readonly _loggerService = inject(LoggerService);

  private _logContext: string = "Thermostat-expanded-section.component";

  public hvacModeEnum = ThermostatHVACMode;
  public isAllHvacModeVisible: boolean = false;
  public isHvacModeProtectionVisible: boolean = false;
  public isHvacModeEconomyVisible: boolean = false;
  public isHvacModeComfortVisible: boolean = false;
  public isHvacModeStandbyVisible: boolean = false;
  public isHvacModeSelectionable: boolean = false;
  public hvacModeSliderMin: number = 0;
  public hvacModeSliderMax: number = 0;
  public singleSetpointForSeason: boolean = false;

  public iconHelper = IconHelper;

  /**The list of displayable (and therefore selectable) HVAC Operative Modes in the proper section of the template. It is important to map
   * the value currently selected on the slider (IE. the current position of the handle) with the associated HVAC Operative Mode, since the (VALUE-1)
   * correspond to the index in the array where the associated HVAC mode has been inserted.
   */
  public definedHvacModes: Array<ThermostatHVACMode> = null;

  public sliderPosition: number = 0;

  public numberOfSteps = 0.1;
  public isWinterColor: boolean = false;

  //Input component adapter thermostat
  @Input() thermostatAdapter: ComponentAdapterThermostat;

  //Input winter color
  @Input() winterColor: ISliderColor;

  //Input summer color
  @Input() summerColor: ISliderColor;

  @Input() isWinter: boolean = false;

  @Input() hvacModeInput: ThermostatHVACMode;
  @Input() hvacModeValues: { [key: string]: number };

  @Input() isFancoilCommandActive: boolean = false;
  @Input() fancoilSpeedValue: number;
  @Input() areStateAndCommandEqual: boolean = false;
  @Input() onSlideFan: (value: number) => void;
  @Input() singleSpeedMode: boolean = false;
  @Input() singleSpeedList: Array<number> = [];
  @Input() singleSpeedSliderMax: number = 0;
  @Input() currentSelectedSpeed: number = 0;
  @Input() automaticFancoilMode: boolean = false;

  ngOnInit(): void {
    this.checkSkillAvailable();
    this.hasOneSetpointForSeason();
  }

  ngOnChanges(): void {
    //isWinterColor follows isWinter only if I do not have a single setpoint, otherwise isWinterColor will be false to obtain the blue color in the sliders, typically used for cooling
    if (!this.thermostatAdapter.HasUniqueSetpointForSeason) {
      this.isWinterColor = this.isWinter;
    }

    // Update hvac mode slider position when something changes
    if (this.definedHvacModes !== null && this.sliderPosition !== this.definedHvacModes.indexOf(this.hvacModeInput) + 1) {
      //There are thermostats that switch to a different mode when certain modes are turned off and
      //this causes the slider to move before settling to the final value. So we add a delay in order to prevent this graphic glitch.
      setTimeout(() => this.sliderPosition = this.definedHvacModes.indexOf(this.hvacModeInput) + 1, 500);
    }
  }

  /**
   * Get name of selected hvac mode
   */
  public getCurrentHvacModeName(): string {
    switch (this.hvacModeInput) {
      case ThermostatHVACMode.COMFORT:
        return this._translateService.instant("dictionary.comfort");
      case ThermostatHVACMode.STANDBY:
        return this._translateService.instant("dictionary.standby");
      case ThermostatHVACMode.ECO:
        return this._translateService.instant("dictionary.economy");
      case ThermostatHVACMode.PROTECTION:
        return this._translateService.instant("dictionary.protection");
      default:
        return "";
    }
  }

  /**
   * Handle callback action when the user changes the winter comfort setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onHeatingComfortChanged(value: number): void {
    if (this.thermostatAdapter.HeatingComfort) {
      if (this.hvacModeValues.heatingComfort != value) {
        this.sendAddressCommand(
          this.thermostatAdapter.HeatingComfort.instanceGUID,
          this.thermostatAdapter.HeatingComfort.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the winter stadby setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onHeatingStandbyChanged(value: number): void {
    if (this.hvacModeValues.heatingStandby != value) {
      if (this.thermostatAdapter.HeatingStandby) {
        this.sendAddressCommand(
          this.thermostatAdapter.HeatingStandby.instanceGUID,
          this.thermostatAdapter.HeatingStandby.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the winter economy setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onHeatingEconomyChanged(value: number): void {
    if (this.hvacModeValues.heatingEconomy != value) {
      if (this.thermostatAdapter.HeatingEconomy) {
        this.sendAddressCommand(
          this.thermostatAdapter.HeatingEconomy.instanceGUID,
          this.thermostatAdapter.HeatingEconomy.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the winter protection setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onHeatingProtectionChanged(value: number): void {
    if (this.thermostatAdapter.HeatingProtection) {
      if (this.hvacModeValues.heatingProtection != value) {
        this.sendAddressCommand(
          this.thermostatAdapter.HeatingProtection.instanceGUID,
          this.thermostatAdapter.HeatingProtection.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the summer comfort setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onCoolingComfortChanged(value: number): void {
    if (this.hvacModeValues.coolingComfort != value) {
      if (this.thermostatAdapter.CoolingComfort) {
        this.sendAddressCommand(
          this.thermostatAdapter.CoolingComfort.instanceGUID,
          this.thermostatAdapter.CoolingComfort.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the summer stadndby setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onCoolingStandbyChanged(value: number): void {
    if (this.hvacModeValues.coolingStandby != value) {
      if (this.thermostatAdapter.CoolingStandby) {
        this.sendAddressCommand(
          this.thermostatAdapter.CoolingStandby.instanceGUID,
          this.thermostatAdapter.CoolingStandby.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the summer economy setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onCoolingEconomyChanged(value: number): void {
    if (this.hvacModeValues.coolingEconomy != value) {
      if (this.thermostatAdapter.CoolingEconomy) {
        this.sendAddressCommand(
          this.thermostatAdapter.CoolingEconomy.instanceGUID,
          this.thermostatAdapter.CoolingEconomy.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Handle callback action when the user changes the summer protection setpoint.
   * If the thermostat adapter is populated, send the command using component adapter properties via mqtt
   *
   * @param value Value received from slider to write address
   */
  public onCoolingProtectionChanged(value: number): void {
    if (this.hvacModeValues.coolingProtection != value) {
      if (this.thermostatAdapter.CoolingProtection) {
        this.sendAddressCommand(
          this.thermostatAdapter.CoolingProtection.instanceGUID,
          this.thermostatAdapter.CoolingProtection.address,
          value.toString()
        );
      }
    }
  }

  /**
   * Callback event for switch automatic fancoil mode
   *
   * @param event Event received from the automatic fancoil switch
   */
  public onSwitchAutoManFancoil(event: Event): void {
    //checked: true is automatic mode
    this.automaticFancoilMode = (event as CustomEvent).detail.checked;

    this.trySendCommandAutomaticFancoil();
  }

  /**
   * Change season when click on season ion-toggle
   * @param event Event received from the season switch
   */
  public onSwitchChangeSeason(event: Event): void {
    //checked: true is winter mode
    this.isWinter = (event as CustomEvent).detail.checked;

    this.trySendCommandSeason();
  }

  /**
   * Change Hvac mode when using slider
   * @param value value received from hvac mode slider
   */
  public onSlideChangeHvacMode(value: number): void {
    if (this.sliderPosition !== value) {
      this.sliderPosition = value;
      const valueMode: ThermostatHVACMode =
        this.definedHvacModes[value - 1];

      this.trySendCommandHVACMode(valueMode);
    }
  }

  /**
   * If command auto-man of fancoil is populated, send the command to the thermostat of fancoil mode
   */
  private trySendCommandAutomaticFancoil(): void {
    if (this.thermostatAdapter?.AutoManCommandFancoil) {
      const instanceGUID: string =
        this.thermostatAdapter.AutoManCommandFancoil.instanceGUID;
      const address: string =
        this.thermostatAdapter.AutoManCommandFancoil.address;

      this.sendAddressCommand(
        instanceGUID,
        address,
        this.automaticFancoilMode ? "1" : "0"
      );
    }
  }

  /**
   * If command season is populated, send the command to the thermostat
   */
  private trySendCommandSeason(): void {
    if (this.thermostatAdapter?.HeatingCoolingCommand) {
      const instanceGUID: string =
        this.thermostatAdapter.HeatingCoolingCommand.instanceGUID;
      const address: string =
        this.thermostatAdapter.HeatingCoolingCommand.address;

      this.sendAddressCommand(instanceGUID, address, this.isWinter ? "1" : "0");
    }
  }

  /**
   * Try to send the command to the thermostat based on the HVAC mode.
   * If the "general HVAC mode" skill has been initialised, the associated command address is used.
   * Otherwise, the address of the skills associated to the single HVAC Operative modes are used.
   *
   * @param hvacMode Hvac mode (selected from the slider) to be sent throught the correct command address.
   */
  private trySendCommandHVACMode(hvacMode: ThermostatHVACMode): void {
    let instanceGUID: string = "";
    let address: string = "";

    if (this.thermostatAdapter?.HvacCommand) {
      instanceGUID = this.thermostatAdapter.HvacCommand.instanceGUID;
      address = this.thermostatAdapter.HvacCommand.address;

      this.sendAddressCommand(instanceGUID, address, hvacMode.toString());
    } else {
      this.trySendCommandHVACSingleMode(hvacMode);
    }
  }

  /**
   * This method uses the command addresses associated to the "single HVAC modes" skills to publish the change to the newly selected HVAC
   * mode of the thermostat.
   * To do so, it consider the following ierarchy:
   *
   * PROTECTION <--- ECONOMY <--- COMFORT / STANDBY
   *
   * Based on which HVAC Mode has newly been selected, it publishes the values (AS STRINGS) either "0" (if it is not the selected mode) or "1"
   * (if it is ) on all the addresses associated to the skills with "higher position" in the ierarchy than the one which has been currently selected.
   *
   * FOR EXAMPLE: if the thermostat has been just set to "ECONOMY", the method will publish "0" on the "ProtectSwitch" skill's address and "1" on the
   *  "EconomySwitch"  skill's address, and nothing on the other skills.
   *
   * @param seletedHVACMode
   */
  private trySendCommandHVACSingleMode(
    seletedHVACMode: ThermostatHVACMode
  ): void {
    if (seletedHVACMode !== this.hvacModeInput) {

      const protectionValue: string =
        seletedHVACMode === ThermostatHVACMode.PROTECTION ? "1" : "0";
      const ecomonyValue: string =
        seletedHVACMode === ThermostatHVACMode.ECO ? "1" : "0";
      const comfortValue: string =
        seletedHVACMode === ThermostatHVACMode.COMFORT ? "1" : "0";
      const standbyValue: string =
        seletedHVACMode === ThermostatHVACMode.STANDBY ? "1" : "0";

      if (this.thermostatAdapter?.ProtectSwitch) {
        this.sendAddressCommand(
          this.thermostatAdapter?.ProtectSwitch.instanceGUID,
          this.thermostatAdapter?.ProtectSwitch.address,
          protectionValue
        );
      }

      if (
        this.thermostatAdapter?.EconomySwitch &&
        seletedHVACMode !== ThermostatHVACMode.PROTECTION
      ) {

        //If standby and comfort are different addresses and one of them is active, turn off this mode before giving economy command
        if ((this.hvacModeInput === ThermostatHVACMode.COMFORT || this.hvacModeInput === ThermostatHVACMode.STANDBY) && (this.thermostatAdapter?.StandbySwitch.instanceGUID !==
          this.thermostatAdapter?.ComfortSwitch.instanceGUID ||
          this.thermostatAdapter?.StandbySwitch.address !==
          this.thermostatAdapter?.ComfortSwitch.address)) {

          const stanbyOrComfortCommand = this.hvacModeInput === ThermostatHVACMode.COMFORT ? "ComfortSwitch" : "StandbySwitch"

          this.sendAddressCommand(
            this.thermostatAdapter?.[stanbyOrComfortCommand].instanceGUID,
            this.thermostatAdapter?.[stanbyOrComfortCommand].address,
            "0"
          );
        }

        this.sendAddressCommand(
          this.thermostatAdapter?.EconomySwitch.instanceGUID,
          this.thermostatAdapter?.EconomySwitch.address,
          ecomonyValue
        );
      }

      //PECULIAR CASE: SEE BELOW.
      if (
        this.thermostatAdapter?.ComfortSwitch &&
        seletedHVACMode !== ThermostatHVACMode.PROTECTION &&
        seletedHVACMode !== ThermostatHVACMode.ECO
      ) {
        this.sendAddressCommand(
          this.thermostatAdapter?.ComfortSwitch.instanceGUID,
          this.thermostatAdapter?.ComfortSwitch.address,
          comfortValue
        );
      }

      //PECULIAR CASE: it is common that "StandbySwitch" and "ComfortSwitch" skills are configured to have the same address; in this case, the command to
      //set the thermostat non mode either "COMFORT" or "STANDBY" is given by writing "1" or "0" ONLY ONE TIME on the single address available.
      //This action is already taken in the step above where we handle the "ComfortSwitch" skill.
      //Therefore, we handle the "StandbySwitch" skill (and write on it the commands) ONLY when its associated address is DIFFERENT FROM THE ONE ASSOCIATED TO "ComfortSwitch" SKILL.
      if (
        this.thermostatAdapter?.StandbySwitch &&
        seletedHVACMode !== ThermostatHVACMode.PROTECTION &&
        seletedHVACMode !== ThermostatHVACMode.ECO &&
        (this.thermostatAdapter?.StandbySwitch.instanceGUID !==
          this.thermostatAdapter?.ComfortSwitch.instanceGUID ||
          this.thermostatAdapter?.StandbySwitch.address !==
          this.thermostatAdapter?.ComfortSwitch.address)
      ) {
        this.sendAddressCommand(
          this.thermostatAdapter?.StandbySwitch.instanceGUID,
          this.thermostatAdapter?.StandbySwitch.address,
          standbyValue
        );
      }
    }
  }

  /**
   * Send generic address command value when skill is commanded
   *
   * @param instancdeGUID Instance GUID of the to command
   * @param address Address to command
   * @param value Value to send over address
   */
  protected sendAddressCommand(
    instanceGUID: string,
    address: string,
    value: string
  ) {
    this._mqttService.publishAddressWrite(
      instanceGUID + "_" + address,
      instanceGUID,
      address,
      "",
      true,
      value
    );
  }

  /**
   * In some cases the thermostats have a single setpoint,
   * for example for Comfort Winter and Comfort Summer they have a single setpoint.
   * This method checks if this is the case to display the labels differently on the screen
   */
  private hasOneSetpointForSeason(): void {
    if (this.thermostatAdapter?.HasUniqueSetpointForSeason) {
      this.singleSetpointForSeason = true;
      //Set isWinterColor because if I have only one setpoint I want the color blue (currently used for the summer season)
      this.isWinterColor = false;
    }
  }

  /**
   * It defines which selectable HVAC Operive modes are showed (and therefore selectable) in the proper section of the graphic template.
   * It does so by checking which "mode-related" skills hav ebeen defined for the current smartCompoent: if the general "HvacCommand" skill
   * has been defined, then all the modes are displayed. Otherwise, the method check the "single-mode" skills.
   */
  private checkSkillAvailable(): void {
    this.hvacModeSliderMin = 1;
    let listDefinedHVACMode = new Array<ThermostatHVACMode>();

    //NOTE: Since "HvacCommand" skill is the one used a fallabck if "HvacState" is missing, we only check it.
    if (this.thermostatAdapter?.HvacCommand) {
      this.isAllHvacModeVisible = true;
      listDefinedHVACMode = Object.keys(ThermostatHVACMode)
        .filter((key) => !isNaN(Number(key)))
        .map((values) => parseInt(values))
        .filter((values) => values > 0);
    } else {
      //CHECK IF "Comfort" SKILL IS DEFINED
      if (this.thermostatAdapter?.ComfortSwitch) {
        this.isHvacModeComfortVisible = true;
        listDefinedHVACMode.push(ThermostatHVACMode.COMFORT);
      }
      //CHECK IF "Standby" SKILL IS DEFINED
      if (this.thermostatAdapter?.StandbySwitch) {
        this.isHvacModeStandbyVisible = true;
        listDefinedHVACMode.push(ThermostatHVACMode.STANDBY);
      }
      //CHECK IF "Economy" SKILL IS DEFINED
      if (this.thermostatAdapter?.EconomySwitch) {
        this.isHvacModeEconomyVisible = true;
        listDefinedHVACMode.push(ThermostatHVACMode.ECO);
      }
      //CHECK IF "Protect" SKILL IS DEFINED
      if (this.thermostatAdapter?.ProtectSwitch) {
        this.isHvacModeProtectionVisible = true;
        listDefinedHVACMode.push(ThermostatHVACMode.PROTECTION);
      }
    }

    this.isHvacModeSelectionable =
      this.isAllHvacModeVisible ||
      this.isHvacModeProtectionVisible ||
      this.isHvacModeEconomyVisible ||
      this.isHvacModeComfortVisible ||
      this.isHvacModeStandbyVisible;
    this.definedHvacModes = listDefinedHVACMode;
    this.hvacModeSliderMax = listDefinedHVACMode.length;

    this.sliderPosition = this.definedHvacModes.indexOf(this.hvacModeInput) + 1;
  }
}
